/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';
import { Table, Loader, DropdownButton } from '../../../Components/uielements';
import { useUserContext } from '../../../hooks/UserContext';
import { EditIcon, EyeIcon } from '../../../Components/icons';
import { managerPremiseList } from '../../../services/bioplus-services/premise';
import { CompanyContainer, TableCell } from './premise.style';

import { EditPremiseModal, ViewAlertReceivers } from './modals';
import { IntlMessages } from '../../../helper/langHelper';
import moment from 'moment';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { exportExcelFile } from '../../../helper/exportExcelHelper';

function extractData(dataArray) {
  if (dataArray) {
    const extractedData = dataArray?.map((item) => {
      return {
        accountOwner: item?.companyName,
        premiseName: item?.propertyName,
        propertyId: item?.propertyId ?? '--',
        address: `${item?.addressStreet}, ${item?.addressTown}, ${item?.addressState}` ?? '--',
        contactNumber: item?.contactNumber ?? '--',
        contactEmail: item?.contactEmail ?? '--',
        createdAt: moment(item?.createdAt).format('DD-MM-YYYY'),
      };
    });
    return { bioplusUsers: extractedData };
  }
}

const ManagerPremise = () => {
  const [editPremise, setEditPremise] = useState(undefined);
  const userContext = useUserContext();
  const userRole = userContext.company;
  const companyId = userRole.companyId?.id;
  const roleId = userContext?.company?._id;
  const premiseId = userRole?.premiseId;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find((permission) => permission?.label === 'premise');

  const canManagePremise = !!isUserAllowToManage?.isManage;
  const canDownloadReport = !!isUserAllowToManage?.isReport;

  const [viewAlertReceivers, setViewAlertReceivers] = useState(undefined);

  const query = useQuery(['managerPremises', page, pageSize], () =>
    managerPremiseList(companyId, premiseId, page, pageSize)
  );

  const tableData = query?.data?.premises ?? [];

  const excelFormatData = extractData(tableData);
  const fileName = `Premises-${userRole?.companyId?.name}-${moment().format(
    'DD-MM-YYYY-hh.mm A'
  )}.xlsx`;
  const fileNameCsv = `Premises-${userRole?.companyId?.name}-${moment().format(
    'DD-MM-YYYY-hh.mm A'
  )}.csv`;

  const columnConfig = [
    { header: 'Account Owner', key: 'accountOwner', width: 30 },
    { header: 'Premise Name', key: 'premiseName', width: 30 },
    { header: 'Property ID', key: 'propertyId', width: 30 },
    { header: 'Address', key: 'address', width: 30 },
    { header: 'Contact Number', key: 'contactNumber', width: 30 },
    { header: 'Contact Email', key: 'contactEmail', width: 30 },
    { header: 'Created Date', key: 'createdAt', width: 30 },
  ];

  const handleClickItem = (item) => {
    // Guard: even if triggered programmatically, block export without report permission
    if (!canDownloadReport) return;

    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  //   table coulmns
  const userColumns = useMemo(() => {
    const cols = [
      {
        Header: <IntlMessages id="table.col.account.owner" />,
        accessor: 'companyName',
        width: '15%',
      },
      {
        Header: <IntlMessages id="table.col.premise.name" />,
        accessor: 'propertyName',
        width: '15%',
      },
      {
        Header: <IntlMessages id="table.col.property.id" />,
        accessor: 'propertyId',
        width: '10%',
        Cell: (cellData) => (
          <TableCell>{`${
            cellData?.row?.original?.propertyId ? cellData?.row?.original?.propertyId : '--'
          }`}</TableCell>
        ),
      },

      {
        Header: <IntlMessages id="table.col.premise.address" />,
        accessor: 'addressState',
        width: '15%',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.addressStreet}, ${cellData?.row?.original?.addressTown}, ${cellData?.row?.original?.addressState}`}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.premise.person.contact" />,
        accessor: 'contactNumber',
        width: '15%',
        Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
      },
      {
        Header: <IntlMessages id="table.col.creator" />,
        accessor: 'contactEmail',
        width: '15%',
        Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
      },
      // {
      //   Header: <IntlMessages id="table.col.alert.receivers" />,
      //   accessor: 'alertReceiverCount',
      //   width: '20%',
      //   Cell: (cellData) => (
      //     <TableCell onClick={() => setViewAlertReceivers(cellData?.row?.original)}>
      //       {cellData.value}{' '}
      //       <EyeIcon style={{ width: '22px', height: '22px', cursor: 'pointer' }} />
      //     </TableCell>
      //   ),
      // },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        width: '15%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
    ];

    // Only show the Actions column (Edit) if the user has manage permission
    if (canManagePremise) {
      cols.push({
        Header: <IntlMessages id="table.col.actions" />,
        accessor: 'actions',
        isAction: true,
        width: '25%',
      });
    }

    return cols;
  }, [canManagePremise]);

  const handleClosedEdit = () => {
    setEditPremise(undefined);
  };
  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const actionList = [
    (data) => (
      <IconButton onClick={() => setEditPremise(data)}>
        <EditIcon style={{ width: '18px', height: '18px' }} />
      </IconButton>
    ),
  ];

  if (query.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <StyledContainer>
      <CompanyContainer>
        <HeaderTitle>
          <IntlMessages id="nav.premises" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '10px' }}>
          {excelFormatData && canDownloadReport && (
            <DropdownButton
              noOfColoumns={1}
              buttonArray={[
                { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                // { key: 'pdf', title: 'Export as PDF', icon: <PictureAsPdfIcon /> },
                { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
              ]}
              onClick={handleClickItem}
            />
          )}
        </TwoColumns>
      </CompanyContainer>

      <TableWrapper>
        <Table
          data={tableData}
          columns={userColumns}
          actions={actionList}
          page={page}
          hasFooter={true}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={query?.data?.totalPremises || 0}
        />
      </TableWrapper>

      {editPremise && editPremise !== undefined && canManagePremise && (
        <EditPremiseModal premise={editPremise} onClose={handleClosedEdit} />
      )}

      {viewAlertReceivers && viewAlertReceivers !== undefined && (
        <ViewAlertReceivers
          onClose={() => setViewAlertReceivers(undefined)}
          premise={viewAlertReceivers}
          isManager={true}
        />
      )}
    </StyledContainer>
  );
};

export default ManagerPremise;
